import {
  QR_EXPIRY_MODE,
  SYSTEM_SETTING_KEY,
  SystemSetting,
  SystemSettingSource,
  SystemSettingValue,
} from "../../../types/system-setting.type";

/** Shown beside every setting whose value feeds report figures. */
export const AFFECTS_REPORTS_WARNING = "มีผลกับรายงานที่สร้างหลังเปลี่ยนเท่านั้น";

export const SETTINGS_LOAD_ERROR = "โหลดการตั้งค่าไม่สำเร็จ กรุณาลองใหม่อีกครั้ง";

/** One page of the change history. */
export const HISTORY_PAGE_SIZE = 10;

export const SOURCE_LABEL: Record<SystemSettingSource, string> = {
  database: "ตั้งค่าแล้ว",
  env: "จาก ENV ของเซิร์ฟเวอร์",
  default: "ค่าเริ่มต้นของระบบ",
};

/** Short Thai names; the server's `description` explains each one in full. */
const SETTING_LABEL: Record<string, string> = {
  [SYSTEM_SETTING_KEY.salesReportWasteMultiplier]: "ตัวคูณยอดควรเบิก (รายงานของเสีย)",
  [SYSTEM_SETTING_KEY.inactiveStoreExcludedBranchNumbers]:
    "ร้านค้าไม่ได้เข้านาน · เลขสาขาที่ไม่นับ",
  [SYSTEM_SETTING_KEY.inactiveStoreExcludedGroups]: "ร้านค้าไม่ได้เข้านาน · GROUP ที่ไม่นับ",
  [SYSTEM_SETTING_KEY.inactiveStoreExcludedCodePrefixes]:
    "ร้านค้าไม่ได้เข้านาน · รหัสลูกค้าขึ้นต้นที่ไม่นับ",
  [SYSTEM_SETTING_KEY.inactiveStoreDefaultDays]: "ร้านค้าไม่ได้เข้านาน · จำนวนวันเริ่มต้น",
  [SYSTEM_SETTING_KEY.inactiveStoreLookbackDays]: "ร้านค้าไม่ได้เข้านาน · ย้อนดูบิล (วัน)",
  [SYSTEM_SETTING_KEY.visitFrequencyAlternatePercent]: "รอบเข้าเยี่ยมร้าน · เกณฑ์วันเว้นวัน (%)",
  [SYSTEM_SETTING_KEY.visitFrequencyTwiceWeeklyPercent]:
    "รอบเข้าเยี่ยมร้าน · เกณฑ์อาทิตย์ละ 2 ครั้ง (%)",
  [SYSTEM_SETTING_KEY.visitFrequencyMinWorkingDays]:
    "รอบเข้าเยี่ยมร้าน · วันทำงานขั้นต่ำที่แบ่งรอบได้",
  [SYSTEM_SETTING_KEY.pivotGapBuckets]: "Pivot · ช่องวันที่ไม่ได้เข้าร้าน (วัน)",
  [SYSTEM_SETTING_KEY.pricebookFacePriceCode]: "สมุดราคาหน้าซอง",
  [SYSTEM_SETTING_KEY.pricebookSalesDefaultCode]: "สมุดราคาของร้านใหม่จากพนักงานขาย",
  [SYSTEM_SETTING_KEY.expressE1StartDate]: "Express · วันเริ่มรับชำระหนี้ (E1)",
  [SYSTEM_SETTING_KEY.customerGeneralGroupCode]: "หมวดลูกค้าทั่วไป",
  [SYSTEM_SETTING_KEY.visitGeofenceRadiusM]: "รัศมีเช็คอินที่ร้าน (เมตร)",
  [SYSTEM_SETTING_KEY.visitCountWasteMaxQty]: "เพดานยอดนับของเสีย (ชิ้น/สินค้า/ร้าน)",
  [SYSTEM_SETTING_KEY.visitCountRemainMaxQty]: "เพดานยอดนับของเหลือ (ชิ้น/สินค้า/ร้าน)",
  [SYSTEM_SETTING_KEY.visitCountWasteConfirmQty]: "ของเสียเกินค่านี้ต้องพิมพ์ยืนยันซ้ำ (ชิ้น)",
  [SYSTEM_SETTING_KEY.visitCountRemainConfirmQty]: "ของเหลือเกินค่านี้ต้องพิมพ์ยืนยันซ้ำ (ชิ้น)",
  [SYSTEM_SETTING_KEY.etaxSubmitDeadlineDay]: "วันสุดท้ายที่ส่ง e-Tax ของเดือนก่อน",
  [SYSTEM_SETTING_KEY.qrExpiryMode]: "วิธีกำหนดอายุ QR ชำระเงิน",
  [SYSTEM_SETTING_KEY.qrExpiryMinutes]: "อายุ QR ชำระเงิน (ระยะเวลา)",
  [SYSTEM_SETTING_KEY.reprintGrantDefaultHours]: "อายุสิทธิ์พิมพ์ซ้ำจากหลังบ้าน (ชั่วโมง)",
  [SYSTEM_SETTING_KEY.reprintRequestValidHours]: "อายุสิทธิ์หลังอนุมัติคำขอพิมพ์ซ้ำ (ชั่วโมง)",
  [SYSTEM_SETTING_KEY.reprintRequestCopyQuantity]: "จำนวนแผ่นต่อสำเนาในคำขอพิมพ์ซ้ำ",
  [SYSTEM_SETTING_KEY.purchaseOrderDeliveryWindowDays]: "ช่วงใช้ PO (วันส่ง ± วัน)",
};

/** A key the server added after this screen was written still shows, under its raw key. */
export const settingLabel = (key: string): string => SETTING_LABEL[key] ?? key;

/** Thai names of a choice setting's values, per key. A value missing here shows as itself. */
const OPTION_LABEL: Record<string, Record<string, string>> = {
  [SYSTEM_SETTING_KEY.qrExpiryMode]: {
    [QR_EXPIRY_MODE.duration]: "ระยะเวลา (ชั่วโมง/นาที)",
    [QR_EXPIRY_MODE.endOfDay]: "สิ้นวัน 23:59:59 (เวลาไทย)",
  },
};

export const settingOptionLabel = (key: string, option: string): string =>
  OPTION_LABEL[key]?.[option] ?? option;

/** Settings held in minutes, edited and shown as hours + minutes. */
const DURATION_MINUTES_KEYS: ReadonlySet<string> = new Set([SYSTEM_SETTING_KEY.qrExpiryMinutes]);

export const isDurationMinutesSetting = (key: string | undefined): boolean =>
  key !== undefined && DURATION_MINUTES_KEYS.has(key);

const QR_MINUTES_UNUSED_NOTE =
  "ตอนนี้ตั้งอายุ QR แบบสิ้นวัน ค่านี้จึงยังไม่ถูกใช้ — จะใช้เมื่อเปลี่ยนกลับเป็นแบบระยะเวลา";

/** A note on a setting that another setting currently makes moot, or null. */
export const settingNote = (setting: SystemSetting, settings: SystemSetting[]): string | null => {
  if (setting.key !== SYSTEM_SETTING_KEY.qrExpiryMinutes) return null;
  const mode = settings.find((other) => other.key === SYSTEM_SETTING_KEY.qrExpiryMode);
  return mode?.value === QR_EXPIRY_MODE.endOfDay ? QR_MINUTES_UNUSED_NOTE : null;
};

/** Count-guard pairs: the confirm threshold must stay below its max. */
const COUNT_GUARD_PAIRS = [
  {
    confirmKey: SYSTEM_SETTING_KEY.visitCountWasteConfirmQty,
    maxKey: SYSTEM_SETTING_KEY.visitCountWasteMaxQty,
  },
  {
    confirmKey: SYSTEM_SETTING_KEY.visitCountRemainConfirmQty,
    maxKey: SYSTEM_SETTING_KEY.visitCountRemainMaxQty,
  },
];

/**
 * A rule between keys the new value breaks, checked against the other key's value now, or
 * null. The server enforces the same rule; this only says it before saving.
 */
export const settingPairError = (
  key: string,
  value: SystemSettingValue,
  settings: SystemSetting[],
): string | null => {
  if (typeof value !== "number") return null;
  const valueOf = (otherKey: string): number | undefined => {
    const other = settings.find((setting) => setting.key === otherKey)?.value;
    return typeof other === "number" ? other : undefined;
  };
  for (const { confirmKey, maxKey } of COUNT_GUARD_PAIRS) {
    if (key === confirmKey) {
      const max = valueOf(maxKey);
      if (max !== undefined && value >= max)
        return `ต้องน้อยกว่า "${settingLabel(maxKey)}" (ตอนนี้ ${max})`;
    }
    if (key === maxKey) {
      const confirm = valueOf(confirmKey);
      if (confirm !== undefined && value <= confirm)
        return `ต้องมากกว่า "${settingLabel(confirmKey)}" (ตอนนี้ ${confirm})`;
    }
  }
  return null;
};
